/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  TasksApi,
  // ControlApi,
  // SkipTaskRequest,
  PlanTaskActualNodeInfoVehicle,
  ResultTaskList,
  ResultTaskLists,
} from "client";
import { useEffect, useState, FC, useMemo, useCallback } from "react";
import { Dialog } from "@mui/material";
import { DialogContainer, MapArea, DetailArea } from "./Util";
import { Map } from "components/Map/Map";
import { NodeMapOverlay, HighlightEdgePolygon } from "../Map/NodeMapOverlay";
import { ResetMapHeading } from "components/Map/ResetMapHeading";
import { ResetMapPosition } from "components/Map/ResetMapPosition";
import { useUserContext } from "context/UserContext";
import { MultipleDetailPanel } from "./MultipleDetailPanel";
import { ConnectingDialog } from "components/Common/ConnectingDialog";
import { ErrorDialog } from "components/Common/ErrorDialog";
import {
  ConfirmDialog,
  ConfirmDialogProps,
} from "components/Common/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { useMapDataContext } from "context/MapDataProvider";
import { TaskCompleteAlert } from "components/Common/TaskCompleteAlert";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { Const } from "const";
import { AutoStartNodeMapOverlay } from "components/Map/AutoStartNodeMapOverlay";
import { TimeoutError } from "exception/TimeoutError";
import { useCancelTask } from "hooks/CancelTaskHook";
import { SpinnerDelay } from "components/Common/SpinnerDelay";
import {
  getErrorResponse,
  getExtendCancelErrMsgCode,
  // getExtendErrMsgCode,
  shouldAddErrMsg,
} from "utils/Api";

interface MultipleDetailDialogProps {
  isOpen: boolean;
  task: ResultTaskList;
  jobs: ResultTaskList[];
  // canSkip?: boolean;
  // onEdited?: (result: TaskData) => void;
  onClose: (isEdited: boolean) => void;
}

export const MultipleDetailDialog: FC<MultipleDetailDialogProps> = ({
  isOpen,
  task,
  jobs,
  // canSkip,
  // onEdited,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mapData } = useMapDataContext();
  const user = useUserContext();
  const [tasksApiForGetTask] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );

  const [isTilesLoaded, setIsTilesLoaded] = useState(false);
  const mapProps = useMemo(() => {
    return {
      mapContainerStyle: {
        height: "100%",
        width: "100%",
      },
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        keyboardShortcuts: false,
        mapId: process.env.REACT_APP_GOOGLEMAP_MAPID,
      },
      center: {
        lat: user.jobsite?.latitude ?? 0,
        lng: user.jobsite?.longitude ?? 0,
      },
      zoom: user.jobsite?.zoom ?? 11,
      onTilesLoaded: () => {
        setIsTilesLoaded(true);
      },
    };
  }, [user.jobsite]);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [confirmContents, /** setConfirmContents */] = useState<ConfirmDialogProps>();
  // const [isEdit, setIsEdit] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedMsg, setCompletedMsg] = useState("");
  const [highlightEdge/** , setHighlightNode */] = useState<
    PlanTaskActualNodeInfoVehicle[]
  >([]);

  const handleCompleteClose = useCallback(() => {
    setIsCompleted(false);
  }, []);

  const cancelTask = useCancelTask();
  const cancelTaskExecute = cancelTask.setTargetTask;
  const [showCancelTaskError, setShowCancelTaskError] = useState(false);

  const dispCancelTaskError = useCallback(
    (addMsg: string) => {
      setErrMsg(
        cancelTask.error instanceof TimeoutError
          ? t("AWVUI-MSG84")
          : t("AWVUI-MSG24") + "\n\n" + t(addMsg)
      );
      // Show the error dialog.
      setShowCancelTaskError(true);
      // Disable spinner
      setIsConnecting(false);
    },
    [cancelTask.error, t]
  );

  const getAddMessage = useCallback((getTaskResult: ResultTaskLists) => {
    return getExtendCancelErrMsgCode(
      getTaskResult?.taskInfos && getTaskResult.taskInfos.length > 0
        ? getTaskResult.taskInfos[0].taskStatus
        : undefined
    );
  }, []);

  useEffect(() => {
    if (!cancelTask.loading && cancelTask.error) {
      // Get error response as Error object.
      getErrorResponse(cancelTask.error).then((errorResponse) => {
        // Get error code.
        let errorCode: string = "";
        if (errorResponse?.errors) {
          errorCode = errorResponse?.errors[0];
        }
        // If the task status is "completed".
        if (shouldAddErrMsg(errorCode)) {
          // Call get task api with the jobet id that was the target of cancellation.
          tasksApiForGetTask
            .getTask({
              frameTypeAndNumberList: [task.frameTypeAndNumber!],
              mapId: mapData.mapId,
              jobSetId: task.jobSetId,
              isApproval: false,
            })
            .then((response) => {
              // Display error dialog.
              dispCancelTaskError(getAddMessage(response));
            })
            .catch((reason) => {
              console.error("reason: %o", reason);
              setErrMsg(
                reason.name === "TimeoutError"
                  ? t("AWVUI-MSG84")
                  : t("AWVUI-MSG24")
              );
              // Show the error dialog.
              setShowCancelTaskError(true);
              setIsConnecting(false);
            });
          // If the task status is not "compleated".
        } else {
          // Get additional text.
          const addMsg = getExtendCancelErrMsgCode(undefined);
          // Display error dialog.
          dispCancelTaskError(addMsg);
        }
      });
    }
  }, [
    cancelTask.error,
    cancelTask.loading,
    dispCancelTaskError,
    getAddMessage,
    mapData.mapId,
    t,
    task,
    tasksApiForGetTask,
  ]);

  useEffect(() => {
    if (cancelTask.value) {
      if (!cancelTask.loading) {
        setIsConnecting(false);
      }
      setCompletedMsg(t("AWVUI-MSG23"));
      setIsCompleted(true);
      onClose(true);
      cancelTaskExecute(undefined);
    }
  }, [cancelTask.loading, cancelTask.value, cancelTaskExecute, onClose, t]);

  const autoStartNodeIds = task.jobInfos
    ?.filter((node) => node.extensionTimeUnload !== undefined)
    .map((node) => node.unloadingNodeId);
  if (task.extensionTimeLoad !== undefined && task.startNodeId !== undefined) {
    autoStartNodeIds?.unshift(task.startNodeId);
  }
  const autoStartNodes = mapData.mapInfo
    .flatMap((mapInfo) => mapInfo.nodes)
    .filter((node) => (node?.id ? autoStartNodeIds?.includes(node.id) : false))
    .filter((elm): elm is Exclude<typeof elm, undefined> => elm !== undefined);

  const targetJobs: ResultTaskList[] = []
  for (const j of jobs) {
    if (j.frameTypeAndNumber === task.frameTypeAndNumber) {
      targetJobs.push(j)
    }
  }

  return (
    <>
      <Dialog
        maxWidth={false}
        fullWidth
        key={isOpen ? "open" : "closed"}
        open={isOpen}
        PaperProps={{ sx: { height: "100%" } }}
      >
        <DialogContainer sidePanelWidth="350px">
          <MapArea>
            <Map {...mapProps}>
              {isTilesLoaded && (
                <>
                  <ResetMapPosition
                    center={mapProps.center}
                    zoom={mapProps.zoom}
                  />
                  <ResetMapHeading />
                </>
              )}
              <NodeMapOverlay mapData={mapData} />
              <HighlightEdgePolygon
                mapData={mapData}
                highlightEdges={highlightEdge}
              />
              <AutoStartNodeMapOverlay nodes={autoStartNodes} />
            </Map>
          </MapArea>
          <DetailArea>
            <MultipleDetailPanel
              task={task}
              jobs={targetJobs}
              // canSkipTask={canSkip ?? false}
              onClose={onClose}
              // onCancelTask={onCancelTask}
              // onEdit={onEdit}
            />
          </DetailArea>
        </DialogContainer>
        {/* <TaskControlDialog
          isOpen={isEdit}
          target={controlTarget}
          onClose={handleControlDialogClose}
        /> */}
        <ConnectingDialog open={isConnecting} />
        <ErrorDialog
          open={isError}
          onPositiveButtonClick={() => {
            setIsError(false);
            onClose(false);
          }}
          message={errMsg}
          positiveButtonText={t("AWVUI-MSG22")}
        />
        {cancelTask.loading && <SpinnerDelay />}
        <ErrorDialog
          open={showCancelTaskError}
          key="cancelTaskError"
          message={errMsg}
          positiveButtonText={t("AWVUI-MSG11")}
          onPositiveButtonClick={() => {
            setShowCancelTaskError(false);
            onClose(true);
            cancelTaskExecute(undefined);
          }}
        />
        {confirmContents && (
          <ConfirmDialog
            {...confirmContents}
            open={confirmContents !== undefined}
          />
        )}
      </Dialog>
      <TaskCompleteAlert
        isOpen={isCompleted}
        onClose={handleCompleteClose}
        label={completedMsg}
      />
    </>
  );
};
